import type Phaser from "phaser";
import { DEPTH } from "../objects/depthLayers.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { FONT_FAMILY, TYPO } from "./uiTokens.js";
import { formatAppElapsed, resolveAppElapsedMs } from "./appTimerFormat.js";

const RIGHT_PAD = 8;
const TOP_PAD = 6;
const BG_PAD_X = 7;
const BG_W_MIN = 58;
const BG_H = 24;

/** 앱 JS가 로드된 시점을 앱 시작 기준으로 삼는다. Scene restart가 나도 유지된다. */
export const APP_TIMER_STARTED_AT_MS = Date.now();

/** DEV 치트용 앱 시작 후 경과 시간 오버레이. */
export class AppTimerOverlay {
  private readonly bg: Phaser.GameObjects.Rectangle;
  private readonly label: Phaser.GameObjects.Text;
  private lastText = "";
  private pausedAtMs: number | null = null;
  private accumulatedPausedMs = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly startedAtMs = APP_TIMER_STARTED_AT_MS,
  ) {
    this.bg = scene.add
      .rectangle(0, 0, BG_W_MIN, BG_H, PALETTE_NUM.cellBg, 0.92)
      .setOrigin(1, 0)
      .setStrokeStyle(1, PALETTE_NUM.accentGold, 0.7)
      .setScrollFactor(0)
      .setDepth(DEPTH.appTimerOverlay);
    this.label = scene.add
      .text(0, 0, "00:00", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textBright,
      })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(DEPTH.appTimerOverlay + 1);
    this.update();
  }

  update(nowMs = Date.now()): void {
    const text = formatAppElapsed(
      resolveAppElapsedMs({
        startedAtMs: this.startedAtMs,
        nowMs,
        accumulatedPausedMs: this.accumulatedPausedMs,
        pausedAtMs: this.pausedAtMs,
      }),
    );
    if (text !== this.lastText) {
      this.lastText = text;
      this.label.setText(text);
      this.bg.width = Math.max(BG_W_MIN, this.label.width + BG_PAD_X * 2);
    }
    this.layout();
  }

  setPaused(paused: boolean, nowMs = Date.now()): void {
    if (paused) {
      if (this.pausedAtMs !== null) return;
      this.update(nowMs);
      this.pausedAtMs = nowMs;
      return;
    }
    if (this.pausedAtMs === null) return;
    this.accumulatedPausedMs += Math.max(0, nowMs - this.pausedAtMs);
    this.pausedAtMs = null;
    this.update(nowMs);
  }

  destroy(): void {
    this.bg.destroy();
    this.label.destroy();
  }

  private layout(): void {
    const right = this.scene.scale.width - RIGHT_PAD;
    this.bg.setPosition(right, TOP_PAD);
    this.label.setPosition(right - BG_PAD_X, TOP_PAD + 5);
  }
}
